import React, { useState } from 'react';
import { FiAlertCircle, FiClock, FiUsers, FiCamera, FiX, FiChevronDown, FiChevronUp, FiLock } from 'react-icons/fi';
import { Modal } from '../../../components';
import { usePermission } from '../../../hooks/usePermission';
import './style.css';

const STATUS_LABELS = {
  em_execucao: 'Em Execução',
  finalizado: 'Finalizado',
  cancelado: 'Cancelado',
};

const PERIOD_LABELS = {
  semanal: 'Semanal',
  mensal: 'Mensal',
  anual: 'Anual',
};

const SERVICE_TYPE_LABELS = {
  cortesia: 'Cortesia',
  terceirizado: 'Terceirizado',
  padrao: 'Padrão',
};

const REQUEST_TYPE_LABELS = {
  corretiva: 'Corretiva',
  preventiva: 'Preventiva',
};

function PhotoViewer({ photo, onClose }) {
  return (
    <div className="so-photo-viewer-overlay" onClick={onClose}>
      <div className="so-photo-viewer-content" onClick={e => e.stopPropagation()}>
        <button className="so-photo-viewer-close" onClick={onClose}>
          <FiX size={20} />
        </button>
        <img src={photo} alt="Foto da execução" />
      </div>
    </div>
  );
}

function ExecutionCard({ exec, expanded, onToggle }) {
  const [viewingPhoto, setViewingPhoto] = useState(null);

  const beforePhotos = (exec.photos || []).filter(p => p.phase === 'before');
  const afterPhotos = (exec.photos || []).filter(p => p.phase === 'after');

  return (
    <div className="so-day-exec-card">
      <div className="so-day-exec-card-header" onClick={onToggle}>
        <div className="so-day-exec-card-info">
          <span className="so-day-exec-title">
            {exec.serviceOrder?.title || exec.serviceOrder?.target_name || `OS #${exec.service_order_id}`}
          </span>
          <span className="so-day-exec-subtitle">
            {exec.executor_name || 'Executor não definido'} | {' '}
            {exec.started_at && new Date(exec.started_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            {exec.finished_at && (
              <> - {new Date(exec.finished_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</>
            )}
          </span>
        </div>
        <div className="so-day-exec-card-actions">
          <span className={`so-execution-status ${exec.status}`}>
            {STATUS_LABELS[exec.status] || exec.status}
          </span>
          {expanded ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
        </div>
      </div>

      {expanded && (
        <div className="so-day-exec-card-body">
          <div className="so-day-exec-detail-row">
            <span className="so-day-exec-detail-label">Tipo de Serviço:</span>
            <span>{SERVICE_TYPE_LABELS[exec.serviceOrder?.service_type] || exec.serviceOrder?.service_type || '-'}</span>
          </div>
          <div className="so-day-exec-detail-row">
            <span className="so-day-exec-detail-label">Tipo de Solicitação:</span>
            <span>{REQUEST_TYPE_LABELS[exec.serviceOrder?.request_type] || exec.serviceOrder?.request_type || '-'}</span>
          </div>

          {exec.serviceOrder?.services?.length > 0 && (
            <div className="so-day-exec-detail-row">
              <span className="so-day-exec-detail-label">Serviços:</span>
              <div className="so-day-services-chips">
                {exec.serviceOrder.services.map((s, i) => (
                  <span key={i} className="so-day-service-chip">{s.name}</span>
                ))}
              </div>
            </div>
          )}

          {exec.observation_before && (
            <div className="so-day-exec-detail-row">
              <span className="so-day-exec-detail-label">Observação (Antes):</span>
              <span className="so-day-exec-observation">{exec.observation_before}</span>
            </div>
          )}

          {exec.observation_after && (
            <div className="so-day-exec-detail-row">
              <span className="so-day-exec-detail-label">Observação (Depois):</span>
              <span className="so-day-exec-observation">{exec.observation_after}</span>
            </div>
          )}

          {exec.status === 'cancelado' && exec.cancel_reason && (
            <div className="so-day-exec-cancel-reason">
              <strong>Motivo do cancelamento:</strong> {exec.cancel_reason}
            </div>
          )}

          {beforePhotos.length > 0 && (
            <div className="so-day-photos-section">
              <span className="so-day-exec-detail-label"><FiCamera size={14} /> Fotos (Antes):</span>
              <div className="so-day-photos-grid">
                {beforePhotos.map((p, i) => (
                  <div key={i} className="so-day-photo-thumb" onClick={() => setViewingPhoto(p.photo)}>
                    <img src={p.photo} alt={`Antes ${i + 1}`} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {afterPhotos.length > 0 && (
            <div className="so-day-photos-section">
              <span className="so-day-exec-detail-label"><FiCamera size={14} /> Fotos (Depois):</span>
              <div className="so-day-photos-grid">
                {afterPhotos.map((p, i) => (
                  <div key={i} className="so-day-photo-thumb" onClick={() => setViewingPhoto(p.photo)}>
                    <img src={p.photo} alt={`Depois ${i + 1}`} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {viewingPhoto && (
        <PhotoViewer photo={viewingPhoto} onClose={() => setViewingPhoto(null)} />
      )}
    </div>
  );
}

export default function DayExecutionsModal({ isOpen, onClose, date, executions, scheduled, isLoading }) {
  const [expandedId, setExpandedId] = useState(null);
  const permissions = usePermission('service_orders');

  const dateStr = date ? new Date(date + 'T00:00:00').toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }) : '';

  const hasScheduled = scheduled && scheduled.length > 0;
  const hasExecutions = executions && executions.length > 0;
  const hasContent = hasScheduled || hasExecutions;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Execuções - ${dateStr}`}>
      {!permissions.canView ? (
        <div className="so-day-modal-empty">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', padding: '20px' }}>
            <FiLock size={40} color="#666" />
            <h3>Acesso Negado</h3>
            <p>Você não tem permissão para visualizar os detalhes desta data.</p>
          </div>
        </div>
      ) : isLoading ? (
        <p className="so-day-modal-empty">
          Carregando...
        </p>
      ) : hasContent ? (
        <div className="so-day-modal-content">
          {/* Scheduled Section */}
          {hasScheduled && (
            <div className="so-day-scheduled-section">
              <div className="so-day-alert-banner">
                <FiAlertCircle size={18} />
                <span>Execuções Agendadas para este dia ({scheduled.length})</span>
              </div>
              {scheduled.map((item, idx) => (
                <div key={idx} className="so-day-scheduled-card">
                  <div className="so-day-scheduled-card-header">
                    <strong>{item.serviceOrder?.target_name || 'Ordem de Serviço'}</strong>
                    <span className="so-day-scheduled-period">
                      {PERIOD_LABELS[item.serviceOrder?.period_type] || item.serviceOrder?.period_type}
                      {item.serviceOrder?.period_quantity > 1 && ` (a cada ${item.serviceOrder.period_quantity})`}
                    </span>
                  </div>

                  {item.serviceOrder?.services?.length > 0 && (
                    <div className="so-day-services-chips">
                      {item.serviceOrder.services.map((s, i) => (
                        <span key={i} className="so-day-service-chip">{s.name}</span>
                      ))}
                    </div>
                  )}

                  <div className="so-day-scheduled-details">
                    {item.serviceOrder?.preferred_time && (
                      <span className="so-day-scheduled-detail">
                        <FiClock size={13} /> {item.serviceOrder.preferred_time}
                      </span>
                    )}
                    {item.serviceOrder?.teamMembers?.length > 0 && (
                      <span className="so-day-scheduled-detail">
                        <FiUsers size={13} /> {item.serviceOrder.teamMembers.map(m => m.entity_name).join(', ')}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Real Executions Section */}
          {hasExecutions && (
            <div className="so-day-executions-section">
              <h4 className="so-day-section-title">Execuções Realizadas ({executions.length})</h4>
              {executions.map((exec) => (
                <ExecutionCard
                  key={exec.id}
                  exec={exec}
                  expanded={expandedId === exec.id}
                  onToggle={() => setExpandedId(expandedId === exec.id ? null : exec.id)}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        <p className="so-day-modal-empty">
          Nenhuma execução ou agendamento neste dia.
        </p>
      )}
    </Modal>
  );
}
